import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  adminApi,
  type ContentExportPathway,
  type ContentExportTrack,
  type GeneratedTrack,
  type GeneratedLesson,
} from '@/api';

export function Content() {
  const navigate = useNavigate();
  const [exportLoading, setExportLoading] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const [aiPrompt, setAiPrompt] = useState('');
  const [aiType, setAiType] = useState<'track' | 'lesson'>('track');
  const [aiTrackId, setAiTrackId] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [generatedTrack, setGeneratedTrack] = useState<GeneratedTrack | null>(null);
  const [generatedLesson, setGeneratedLesson] = useState<GeneratedLesson | null>(null);
  const [tracks, setTracks] = useState<ContentExportTrack[]>([]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const data = await adminApi.content.export();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `content-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const handleImport = async () => {
    setImportError(null);
    setImportResult(null);
    setImportLoading(true);
    try {
      const parsed = JSON.parse(importJson) as { pathways?: ContentExportPathway[]; tracks?: ContentExportTrack[] };
      const pathwaysToImport = parsed.pathways ?? [];
      const tracksToImport = parsed.tracks ?? (Array.isArray(parsed) ? parsed : []);
      if (!Array.isArray(tracksToImport) || tracksToImport.length === 0) {
        throw new Error('JSON must have a "tracks" array with at least one track.');
      }
      const achievementsToImport = (parsed as { achievements?: any[] }).achievements ?? [];
      const result = await adminApi.content.import({
        pathways: Array.isArray(pathwaysToImport) ? pathwaysToImport : [],
        tracks: tracksToImport,
        achievements: Array.isArray(achievementsToImport) ? achievementsToImport : [],
      });
      setImportResult(
        `Imported: ${result.pathwaysCreated} pathway(s), ${result.tracksCreated} track(s), ${result.lessonsCreated} lesson(s), ${result.questionsCreated} question(s), ${result.achievementsCreated} achievement(s).`,
      );
      setImportJson('');
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  const handleGenerateAi = async () => {
    setAiError(null);
    setGeneratedTrack(null);
    setGeneratedLesson(null);
    setAiLoading(true);
    try {
      if (aiType === 'track') {
        const t = await adminApi.ai.generateTrack(aiPrompt);
        setGeneratedTrack(t);
      } else {
        const l = await adminApi.ai.generateLesson(aiPrompt, aiTrackId || undefined);
        setGeneratedLesson(l);
      }
    } catch (e) {
      setAiError(e instanceof Error ? e.message : 'AI generation failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handleCreateGeneratedTrack = async () => {
    if (!generatedTrack) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const pathways = await adminApi.pathways.list();
      const pathwayId = pathways.find((p) => p.name === generatedTrack.pathway)?.id ?? pathways[0]?.id ?? 'pathway-foundation';
      const icon = (generatedTrack as { icon?: string }).icon ?? generatedTrack.lessons[0]?.icon ?? 'Zap';
      const created = await adminApi.tracks.create({
        id: generatedTrack.id,
        name: generatedTrack.name,
        description: generatedTrack.description,
        icon,
        pathwayId,
        difficulty: generatedTrack.difficulty,
        color: generatedTrack.color,
      });
      for (let i = 0; i < generatedTrack.lessons.length; i++) {
        const l = generatedTrack.lessons[i]!;
        await adminApi.lessons.create({
          id: l.id,
          trackId: created.id,
          title: l.title,
          description: l.description,
          icon: l.icon,
          order: i,
        });
      }
      setGeneratedTrack(null);
      setAiPrompt('');
      navigate('/tracks');
    } catch (e) {
      setAiError(e instanceof Error ? e.message : 'Failed to create track');
    } finally {
      setAiLoading(false);
    }
  };

  const handleCreateGeneratedLesson = async () => {
    if (!generatedLesson) return;
    const trackId = generatedLesson.trackId || aiTrackId;
    if (!trackId) {
      setAiError('Select a track or generate a lesson that includes trackId.');
      return;
    }
    setAiLoading(true);
    setAiError(null);
    try {
      await adminApi.lessons.create({
        id: generatedLesson.id,
        trackId,
        title: generatedLesson.title,
        description: generatedLesson.description,
        icon: generatedLesson.icon,
        order: generatedLesson.order,
      });
      setGeneratedLesson(null);
      setAiPrompt('');
      navigate('/lessons');
    } catch (e) {
      setAiError(e instanceof Error ? e.message : 'Failed to create lesson');
    } finally {
      setAiLoading(false);
    }
  };

  const loadTracksForSelect = async () => {
    if (tracks.length > 0) return;
    try {
      const list = await adminApi.content.export();
      setTracks(list.tracks);
    } catch {
      // ignore
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-slate-800 mb-6">Content</h1>

      <section className="mb-10">
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Design with AI</h2>
        <p className="text-sm text-slate-600 mb-4">
          Generate a track or lesson from a short prompt. Review the result, then create it.
        </p>
        <div className="flex flex-wrap gap-3 items-end mb-4">
          <select
            value={aiType}
            onChange={(e) => {
              setAiType(e.target.value as 'track' | 'lesson');
              setGeneratedTrack(null);
              setGeneratedLesson(null);
            }}
            className="rounded-lg border border-slate-300 px-3 py-2"
          >
            <option value="track">Generate track</option>
            <option value="lesson">Generate lesson</option>
          </select>
          {aiType === 'lesson' && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">Track:</label>
              <select
                value={aiTrackId}
                onChange={(e) => setAiTrackId(e.target.value)}
                onFocus={loadTracksForSelect}
                className="rounded-lg border border-slate-300 px-3 py-2 min-w-[180px]"
              >
                <option value="">Select track</option>
                {tracks.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}
          <input
            type="text"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder={aiType === 'track' ? 'e.g. Intro to machine learning for beginners' : 'e.g. What is a neural network?'}
            className="rounded-lg border border-slate-300 px-3 py-2 min-w-[240px]"
          />
          <button
            type="button"
            onClick={handleGenerateAi}
            disabled={aiLoading || !aiPrompt.trim()}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-white font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {aiLoading ? 'Generating…' : 'Generate'}
          </button>
        </div>
        {aiError && <p className="text-red-600 text-sm mb-2">{aiError}</p>}
        {generatedTrack && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4">
            <p className="font-medium text-slate-800">{generatedTrack.name}</p>
            <p className="text-sm text-slate-600 mt-1">{generatedTrack.description}</p>
            <p className="text-sm text-slate-500 mt-2">{generatedTrack.lessons.length} lessons</p>
            <button
              type="button"
              onClick={handleCreateGeneratedTrack}
              disabled={aiLoading}
              className="mt-3 rounded-lg bg-emerald-600 px-4 py-2 text-white text-sm font-medium hover:bg-emerald-700"
            >
              Create track & lessons
            </button>
          </div>
        )}
        {generatedLesson && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4">
            <p className="font-medium text-slate-800">{generatedLesson.title}</p>
            <p className="text-sm text-slate-600 mt-1">{generatedLesson.description}</p>
            <button
              type="button"
              onClick={handleCreateGeneratedLesson}
              disabled={aiLoading}
              className="mt-3 rounded-lg bg-emerald-600 px-4 py-2 text-white text-sm font-medium hover:bg-emerald-700"
            >
              Create lesson
            </button>
          </div>
        )}
      </section>

      <section>
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Import / Export</h2>
        <p className="text-sm text-slate-600 mb-4">
          Export all pathways, tracks, lessons, questions, and achievements as JSON. Import from a previously exported JSON (or compatible format).
        </p>
        <div className="flex gap-4 mb-4">
          <button
            type="button"
            onClick={handleExport}
            disabled={exportLoading}
            className="rounded-lg bg-slate-700 px-4 py-2 text-white font-medium hover:bg-slate-800 disabled:opacity-50"
          >
            {exportLoading ? 'Exporting…' : 'Download JSON'}
          </button>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Import from JSON</label>
          <textarea
            value={importJson}
            onChange={(e) => setImportJson(e.target.value)}
            placeholder='Paste JSON with "pathways", "tracks", and "achievements" arrays (from export).'
            rows={8}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
          />
          <button
            type="button"
            onClick={handleImport}
            disabled={importLoading || !importJson.trim()}
            className="mt-2 rounded-lg bg-indigo-600 px-4 py-2 text-white font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {importLoading ? 'Importing…' : 'Import'}
          </button>
          {importResult && <p className="mt-2 text-emerald-600 text-sm">{importResult}</p>}
          {importError && <p className="mt-2 text-red-600 text-sm">{importError}</p>}
        </div>
      </section>
    </div>
  );
}
